// Friendly labels for each backend effectiveState. Kept plain-language so the
// Operations team never has to read raw state machine terms.
export const EFFECTIVE_STATE_LABELS = {
  pending: "Pending activation",
  awaiting_external_activation: "Waiting on LendingPad",
  testing: "Testing",
  connected: "Connected",
  syncing: "Syncing",
  failed: "Activation failed",
  degraded: "Degraded",
  disabled: "Disabled",
};

// Per-state presentation: badge/banner CSS class, icon, short human title and a
// one-line description. Colours live in Integrations.css (theme-aware).
export const STATE_META = {
  connected: {
    cls: "connected",
    icon: "✓",
    title: "Connected",
    desc: "Live and syncing loans from LendingPad automatically.",
  },
  pending: {
    cls: "pending",
    icon: "◷",
    title: "Ready to activate",
    desc: "Configuration is saved. Activate to go live.",
  },
  awaiting_external_activation: {
    cls: "awaiting",
    icon: "⧗",
    title: "Waiting on LendingPad",
    desc: "LendingPad still needs to enable data access for this organization.",
  },
  testing: {
    cls: "testing",
    icon: "↻",
    spin: true,
    title: "Checking the connection",
    desc: "Running health checks against LendingPad…",
  },
  syncing: {
    cls: "syncing",
    icon: "↻",
    spin: true,
    title: "Sync in progress",
    desc: "Importing loans from LendingPad.",
  },
  failed: {
    cls: "failed",
    icon: "✕",
    title: "Activation failed",
    desc: "Something went wrong while connecting. See the details below.",
  },
  degraded: {
    cls: "degraded",
    icon: "!",
    title: "Connected, but delayed",
    desc: "The connection works, but recent data may be behind.",
  },
  disabled: {
    cls: "disabled",
    icon: "⏸",
    title: "Turned off",
    desc: "This integration was switched off by Operations. History is kept.",
  },
};

export function stateMeta(state) {
  return STATE_META[state] || STATE_META.pending;
}

/**
 * Presentation for the inbound export feed — the signal that actually answers
 * "is data still arriving?". Kept separate from the connection test, which only
 * proves the API answers when we call it.
 */
export const FEED_META = {
  live: {
    cls: "connected",
    icon: "●",
    label: "Live",
    desc: "LendingPad is delivering updates to Addy.",
  },
  silent: {
    cls: "degraded",
    icon: "!",
    label: "Silent",
    desc: "No delivery has arrived recently. Updates made in LendingPad are not reaching Addy.",
  },
  none_recorded: {
    cls: "pending",
    icon: "◷",
    label: "None recorded",
    desc: "No delivery has been recorded yet for this organization.",
  },
};

export function feedMeta(status) {
  return FEED_META[status] || FEED_META.none_recorded;
}

/**
 * Presentation for a sync-history row's origin — what caused the row to exist.
 *
 * Two things happen for every loan that flows in, and both are recorded:
 * LendingPad notifies us that a loan changed (the delivery), then Addy fetches
 * that loan and writes it (the import). Deliveries arriving is what proves the
 * feed is alive; imports alone mean something other than LendingPad is driving
 * the data — usually an operator pressing Sync Data.
 *
 * Imports carry a `syncSource` naming what triggered them, so a row can say
 * *why* it ran and not merely that it did.
 */
export const ORIGIN_META = {
  delivery: {
    cls: "webhook",
    label: "Delivery",
    summary: "LendingPad told Addy this loan changed.",
    detail:
      "Deliveries arriving are what prove the feed is alive. No deliveries means updates aren't reaching Addy, however healthy the connection looks.",
  },
  import_webhook: {
    cls: "worker",
    label: "Import · from delivery",
    summary: "Addy fetched the loan after LendingPad announced a change.",
    detail: "The normal, healthy path — one per delivery.",
  },
  import_backfill: {
    cls: "backfill",
    label: "Import · backfill",
    summary: "Pulled during a whole-organization sync.",
    detail:
      "Runs on activation and on Sync Data. An org kept current only by backfills has a dead feed.",
  },
  import_manual: {
    cls: "manual",
    label: "Import · manual",
    summary: "Someone re-imported this specific loan.",
    detail: "",
  },
  import_unknown: {
    cls: "worker",
    label: "Import",
    summary: "Addy fetched and wrote the loan.",
    detail: "Recorded before import causes were tracked.",
  },
};

/**
 * Put stage names into a sync-history reason.
 *
 * Entries written before stage names were included read "status 1 excluded from
 * sync", which makes an operator look the id up. The stage catalog is already
 * loaded for the Import stages tab, so history can be labelled retroactively
 * rather than staying half-readable until it ages out.
 *
 * @param {string} reason - Raw reason from the sync-history entry.
 * @param {Array<{id: number, name: string}>} availableStages
 * @returns {string}
 */
export function describeSyncReason(reason, availableStages = []) {
  if (!reason) {
    return "";
  }

  const nameById = new Map(availableStages.map((stage) => [stage.id, stage.name]));

  return reason.replace(/\bstatus (\d+)\b/gi, (match, id) => {
    const name = nameById.get(Number(id));

    return name ? `${name} (${id})` : match;
  });
}

/**
 * Resolve a sync-history row to its origin presentation.
 * @param {{ triggeredBy?: string, syncSource?: string }} entry
 */
export function originMeta(entry = {}) {
  if (entry.triggeredBy === "webhook") {
    return ORIGIN_META.delivery;
  }

  if (entry.triggeredBy === "worker") {
    return (
      ORIGIN_META[`import_${entry.syncSource}`] || ORIGIN_META.import_unknown
    );
  }

  return {
    cls: "unknown",
    label: entry.triggeredBy || "—",
    summary: "Unrecognized origin.",
    detail: "",
  };
}

// How a Company ID shows beside its alias: a full UUID is noise in a list.
export function shortCompanyId(companyId) {
  if (!companyId) {
    return "";
  }

  return companyId.length > 8 ? `${companyId.slice(0, 8)}…` : companyId;
}

export const EFFECTIVE_STATE_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "connected", label: "Connected" },
  { value: "pending", label: "Pending activation" },
  { value: "awaiting_external_activation", label: "Waiting on LendingPad" },
  { value: "failed", label: "Activation failed" },
  { value: "syncing", label: "Syncing" },
  { value: "degraded", label: "Degraded" },
  { value: "disabled", label: "Disabled" },
];

/**
 * Resolves the single primary action for an organization based on its state.
 * Unifies "Activate" and "Retry activation" into one primary button whose label
 * and target endpoint change with state (routing to /activate when there is a
 * fresh passing test, otherwise /retry-activation which tests + activates).
 *
 * Returns: { key, label, icon, tip, disabled?, loading?, primaryIsTest? }
 *   key "activate" | "sync" | "test" | "none"
 */
export function resolvePrimaryAction(detail) {
  const state = detail?.effectiveState;
  const canSync = detail?.canRetryBackfill === true;

  switch (state) {
    case "connected":
    case "degraded":
      return canSync
        ? {
            key: "sync",
            label: "Sync Data",
            icon: "⇅",
            tip: "Import the latest loans from LendingPad now.",
          }
        : {
            key: "test",
            label: "Test Connection",
            icon: "⇄",
            primaryIsTest: true,
            tip: "Re-check the live connection.",
          };
    case "syncing":
    case "testing":
      return {
        key: "none",
        label: state === "syncing" ? "Syncing…" : "Testing…",
        disabled: true,
        loading: true,
      };
    case "failed":
      return {
        key: "activate",
        label: "Retry Activation",
        icon: "↻",
        tip: "Run the checks again and activate if they pass.",
      };
    case "awaiting_external_activation":
      return {
        key: "activate",
        label: "Retry Activation",
        icon: "↻",
        tip: "Retry once LendingPad has enabled access.",
      };
    case "disabled":
      return {
        key: "activate",
        label: "Re-activate",
        icon: "⚡",
        tip: "Turn the integration back on.",
      };
    case "pending":
    default:
      return {
        key: "activate",
        label: "Activate Integration",
        icon: "⚡",
        tip: "Test the connection and go live in one step.",
      };
  }
}

// Resolved once: the operator's own zone, appended to rendered timestamps.
// LendingPad reports UTC and Firestore stores UTC, so an unlabelled local-time
// string is the easiest way to miscommunicate a timeline to the vendor.
export const LOCAL_TIME_ZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (_error) {
    return "";
  }
})();

export function formatDate(millis) {
  if (!millis) {
    return "—";
  }

  try {
    return new Date(millis).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_error) {
    return "—";
  }
}

/**
 * Full, unambiguous rendering for tooltips: local time plus the UTC value, so
 * an operator can quote a timestamp to LendingPad without converting anything.
 */
export function formatDatePrecise(millis) {
  if (!millis) {
    return "";
  }

  try {
    const local = formatDate(millis);
    const utc = new Date(millis).toISOString().replace("T", " ").slice(0, 16);

    return `${local}${LOCAL_TIME_ZONE ? ` (${LOCAL_TIME_ZONE})` : ""} · ${utc} UTC`;
  } catch (_error) {
    return "";
  }
}

export function formatRelativeTime(millis) {
  if (!millis) {
    return "—";
  }

  const deltaMs = Date.now() - millis;
  const minutes = Math.floor(deltaMs / 60000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 48) {
    return `${hours}h ago`;
  }

  return formatDate(millis);
}
